/**
 * Build styles
 */
require('./index.css').toString();
import { v4 as uuidv4 } from 'uuid';

/**
 * @class Warning
 * @classdesc Quiz Question for Editor.js
 * @property {WarningData} data - Quiz Question`s input and output data
 * @property {object} api - Editor.js API instance
 *
 * @typedef {object} WarningData
 * @description Quiz Question`s input and output data
 * @property {string} title - warning`s title
 * @property {string} message - warning`s message
 *
 * @typedef {object} WarningConfig
 * @description Quiz Question`s initial configuration
 * @property {string} titlePlaceholder - placeholder to show in warning`s title input
 * @property {string} messagePlaceholder - placeholder to show in warning`s message input
 */
export default class Warning {

    /**
     * Notify core that read-only mode is supported
     */
    static get isReadOnlySupported() {
        return true;
    }

    /**
     * Get Toolbox settings
     *
     * @public
     * @returns {string}
     */
    static get toolbox() {
        return {
            icon: '<svg width="18" height="18" aria-hidden="true" focusable="false" data-prefix="far" data-icon="question-square" class="svg-inline--fa fa-question-square fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M448 80v352c0 26.51-21.49 48-48 48H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48zm-48 346V86a6 6 0 0 0-6-6H54a6 6 0 0 0-6 6v340a6 6 0 0 0 6 6h340a6 6 0 0 0 6-6zm-68.756-225.2c0 67.052-72.421 68.084-72.421 92.863V300c0 6.627-5.373 12-12 12h-45.647c-6.627 0-12-5.373-12-12v-8.659c0-35.745 27.1-50.034 47.579-61.516 17.561-9.845 28.324-16.541 28.324-29.579 0-17.246-21.999-28.693-39.784-28.693-23.189 0-33.894 10.977-48.942 29.969-4.057 5.12-11.46 6.071-16.666 2.124l-27.824-21.098c-5.107-3.872-6.251-11.066-2.644-16.363C152.846 131.491 182.94 112 229.794 112c49.071 0 101.45 38.304 101.45 88.8zM266 368c0 23.159-18.841 42-42 42s-42-18.841-42-42 18.841-42 42-42 42 18.841 42 42z"></path></svg>',
            title: 'Quiz Question'
        };
    }

    /**
     * Allow to press Enter inside the Warning
     *
     * @public
     * @returns {boolean}
     */
    static get enableLineBreaks() {
        return true;
    }

    /**
     * Default placeholder for warning title
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_TITLE_PLACEHOLDER() {
        return 'Question';
    }

    /**
     * Default placeholder for warning message
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_MESSAGE_PLACEHOLDER() {
        return 'Description & Help';
    }

    /**
     * Quiz Question`s styles
     *
     * @returns {object}
     */
    get CSS() {
        return {
            baseClass: this.api.styles.block,
            wrapper: 'ejsqq',
            title: 'cdx-warning__title',
            input: this.api.styles.input,
            message: 'cdx-warning__message',
        };
    }

    /**
     * Render plugin`s main Element and fill it with saved data
     *
     * @param {WarningData} data — previously saved data
     * @param {WarningConfig} config — user config for Tool
     * @param {object} api - Editor.js API
     * @param {boolean} readOnly - read-only mode flag
     */
    constructor({ data, config, api, readOnly }) {
        this.api = api;
        this.readOnly = readOnly;

        this.titlePlaceholder = config.titlePlaceholder || Warning.DEFAULT_TITLE_PLACEHOLDER;
        this.messagePlaceholder = config.messagePlaceholder || Warning.DEFAULT_MESSAGE_PLACEHOLDER;

        this.data = {
            title: data.title || '',
            message: data.message || '',
            id: data.id || uuidv4()
        };
    }

    /**
     * Create Quiz Question container with inputs
     *
     * @returns {Element}
     */
    render() {
        const container = this._make('div', [this.CSS.baseClass, this.CSS.wrapper]);
        const title = this._make('div', [this.CSS.input, this.CSS.title], {
            contentEditable: !this.readOnly,
            innerHTML: this.data.title,
        });
        const message = this._make('div', [this.CSS.input, this.CSS.message], {
            contentEditable: !this.readOnly,
            innerHTML: this.data.message,
        });

        const type = this._make('select', [this.CSS.input, 'cdx-field-type'], {});


        let option;

        option = document.createElement("option");
        option.value = 'Text Field';
        option.text = 'Text Field';
        type.appendChild(option);

        option = document.createElement("option");
        option.value = 'Large Text Area';
        option.text = 'Large Text Area';
        type.appendChild(option);

        title.dataset.placeholder = this.titlePlaceholder;
        message.dataset.placeholder = this.messagePlaceholder;

        container.appendChild(title);
        container.appendChild(message);
        container.appendChild(type);

        return container;
    }

    /**
     * Extract Warning data from Quiz Question element
     *
     * @param {HTMLDivElement} warningElement - element to save
     * @returns {WarningData}
     */
    save(warningElement) {
        const title = warningElement.querySelector(`.${this.CSS.title}`);
        const message = warningElement.querySelector(`.${this.CSS.message}`);
        const fType = warningElement.querySelector(`.field-type`);

        console.log(fType, fType.value);

        return Object.assign(this.data, {
            title: title.innerHTML,
            message: message.innerHTML,
            fieldType: fType.value
        });
    }

    /**
     * Helper for making Elements with attributes
     *
     * @param  {string} tagName           - new Element tag name
     * @param  {Array|string} classNames  - list or name of CSS classname(s)
     * @param  {object} attributes        - any attributes
     * @returns {Element}
     */
    _make(tagName, classNames = null, attributes = {}) {
        const el = document.createElement(tagName);

        if (Array.isArray(classNames)) {
            el.classList.add(...classNames);
        } else if (classNames) {
            el.classList.add(classNames);
        }

        for (const attrName in attributes) {
            el[attrName] = attributes[attrName];
        }

        return el;
    }

    /**
     * Sanitizer config for Quiz Question saved data
     *
     * @returns {object}
     */
    static get sanitize() {
        return {
            title: {},
            message: {},
        };
    }
}
